import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SiteRenderer from 'dashboard/components/siteRenderer/SiteRenderer';

/**
 * The page as a visitor sees it, with the sections made selectable.
 *
 * It renders the real `SiteRenderer`, not a diagram of it. The old editor
 * showed a list of collapsible cards full of dropdowns, and the page those
 * settings produced was somewhere else entirely — you edited "section 3,
 * version v1, background #0f172a" and then went to look at what you had done.
 * Here the thing on screen IS the page, and clicking a part of it is how you
 * choose what to edit.
 *
 * Selection is by hit-testing `data-section-id` on the rendered DOM rather
 * than by rebuilding the layout in editor components. That is the whole point:
 * there is one renderer, so the editor cannot drift away from the published
 * page, and a new section type works here the day it renders.
 *
 * Interaction inside the canvas is suppressed — links and buttons are part of
 * the page being edited, not controls. Clicking "Shop now" should select that
 * section, not navigate away from the editor.
 */
export default function PageCanvas({
  page, brand, selectedId, onSelect,
  onMoveSection, onRemoveSection, onToggleSection, onAddBelow, onDropSection,
}) {
  const hostRef = useRef(null);
  const [rect, setRect] = useState(null);
  const [hoverId, setHoverId] = useState(null);
  // Drag state lives here rather than on the DOM: the canvas re-renders on
  // every selection, and an attribute set by a dragstart handler would not
  // survive that.
  const [dragId, setDragId] = useState(null);
  const [dropId, setDropId] = useState(null);

  const sections = (page?.sectionData || []);
  const selected = sections.find((s) => s._id === selectedId) || null;

  // The toolbar floats over the selected section, so it has to follow the
  // section through scrolling, window resizes and its own content changing
  // height as you edit it.
  const measure = useCallback(() => {
    const host = hostRef.current;
    if (!host || !selectedId) { setRect(null); return; }
    const el = host.querySelector(`[data-section-id="${selectedId}"]`);
    if (!el) { setRect(null); return; }
    const hostBox = host.getBoundingClientRect();
    const box = el.getBoundingClientRect();
    setRect({
      top: box.top - hostBox.top,
      left: box.left - hostBox.left,
      width: box.width,
      height: box.height,
    });
  }, [selectedId]);

  useEffect(() => {
    measure();
    const host = hostRef.current;
    if (!host) return undefined;
    // ResizeObserver catches the section growing as its text is edited, which
    // a scroll/resize listener alone would miss.
    const ro = new ResizeObserver(measure);
    ro.observe(host);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [measure, page]);

  const idFrom = (e) => e.target?.closest?.('[data-section-id]')?.dataset?.sectionId || null;

  const index = selected ? sections.findIndex((s) => s._id === selected._id) : -1;

  return (
    <Box
      ref={hostRef}
      onClick={(e) => {
        // Everything inside the canvas is content, not controls.
        e.preventDefault();
        const id = idFrom(e);
        onSelect(id);
      }}
      onMouseOver={(e) => setHoverId(idFrom(e))}
      onMouseLeave={() => setHoverId(null)}
      onDragOver={(e) => {
        if (!dragId) return;
        e.preventDefault();
        const over = idFrom(e);
        // Only within the same region: a footer cannot become the second
        // thing on the page, and allowing the drop would silently do nothing.
        const from = sections.find((x) => x._id === dragId);
        const to = sections.find((x) => x._id === over);
        setDropId(to && from && to.region === from.region && to._id !== from._id ? over : null);
      }}
      onDrop={(e) => {
        e.preventDefault();
        if (dragId && dropId) onDropSection(dragId, dropId);
        setDragId(null);
        setDropId(null);
      }}
      sx={{
        position: 'relative',
        borderRadius: '12px',
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        // White in both modes on purpose: this is the visitor's site, and a
        // preview that followed the dashboard's colour mode would show the
        // owner a page no visitor will ever see.
        bgcolor: '#fff',
        // Stops a click inside the page from dragging an image or selecting
        // text when the intent was to pick a section.
        userSelect: 'none',
        '& a, & button': { pointerEvents: 'none' },
      }}
    >
      {/* editorMode dims hidden sections and badges them. They have to stay
          on the canvas — a hidden section is usually the one being built —
          but must not look like something visitors can see. */}
      <SiteRenderer page={page} brand={brand} editorMode />

      {dropId && (
        <SectionOutline hostRef={hostRef} id={dropId} color='rgba(16,185,129,0.9)' />
      )}

      {/* Hover hint, drawn under the selection so the two never fight. */}
      {hoverId && hoverId !== selectedId && (
        <SectionOutline
          hostRef={hostRef}
          id={hoverId}
          color='rgba(79,70,229,0.45)'
          dashed
        />
      )}

      {/**
        * Both, not just the rectangle.
        *
        * `rect` is measured by an effect and `selected` is derived from the
        * section list, so they can disagree for a render: delete a section,
        * or reload the page's data, and `selected` becomes null at once while
        * the measured rectangle survives until the effect runs again.
        *
        * This used to be guarded on `rect` alone, which was safe only because
        * nothing inside read `selected` during the render — it appeared in
        * click handlers, which do not run then. The moment the toolbar needed
        * `selected.isVisible` to label its own button, that gap became a
        * crash that took the whole dashboard down rather than one toolbar.
        */}
      {rect && selected && (
        <>
          <Box
            sx={{
              position: 'absolute',
              top: rect.top, left: rect.left, width: rect.width, height: rect.height,
              outline: '2px solid', outlineColor: 'primary.main', outlineOffset: '-2px',
              borderRadius: '2px',
              pointerEvents: 'none',
            }}
          />
          <Stack
            direction='row'
            spacing='2px'
            sx={{
              position: 'absolute',
              // Sits inside the section when it is at the very top of the
              // page, where an above-the-edge toolbar would be clipped away.
              top: Math.max(4, rect.top - 34),
              left: rect.left + 8,
              bgcolor: 'background.paper',
              border: '1px solid', borderColor: 'divider',
              borderRadius: '8px',
              boxShadow: 2,
              p: '2px',
              zIndex: 5,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* The toolbar is the drag handle. Dragging the section body
                itself would fight text selection and image dragging inside
                the page being edited. */}
            <Box
              draggable
              onDragStart={(e) => {
                setDragId(selected._id);
                e.dataTransfer.effectAllowed = 'move';
                // Firefox refuses to start a drag without data set.
                e.dataTransfer.setData('text/plain', selected._id);
              }}
              onDragEnd={() => { setDragId(null); setDropId(null); }}
              sx={{
                display: 'flex', alignItems: 'center', gap: '4px',
                px: '8px', cursor: 'grab', color: 'text.secondary',
                '&:active': { cursor: 'grabbing' },
              }}
            >
              <DragIndicatorIcon fontSize='small' />
              <Typography variant='caption' sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                {selected?.name || selected?.type}
              </Typography>
            </Box>
            <CanvasBtn label='Move up' disabled={index <= 0} onClick={() => onMoveSection(selected, -1)}>
              <ArrowUpwardIcon fontSize='small' />
            </CanvasBtn>
            <CanvasBtn
              label='Move down'
              disabled={index < 0 || index >= sections.length - 1}
              onClick={() => onMoveSection(selected, 1)}
            >
              <ArrowDownwardIcon fontSize='small' />
            </CanvasBtn>
            <CanvasBtn label='Add section below' onClick={() => onAddBelow(selected)}>
              <AddIcon fontSize='small' />
            </CanvasBtn>
            {/* Says what the click will do, not what the section is. It
                read "Hide from the live site" in both states, so pressing it
                to bring a section back said the opposite of what it did and
                the control looked broken — the section really was toggling
                the whole time. */}
            <CanvasBtn
              label={selected.isVisible === false ? 'Show on the live site' : 'Hide from the live site'}
              onClick={() => onToggleSection(selected)}
            >
              {selected.isVisible === false
                ? <VisibilityIcon fontSize='small' />
                : <VisibilityOffIcon fontSize='small' />}
            </CanvasBtn>
            <CanvasBtn label='Delete' danger onClick={() => onRemoveSection(selected)}>
              <DeleteIcon fontSize='small' />
            </CanvasBtn>
          </Stack>
        </>
      )}
    </Box>
  );
}

function CanvasBtn({ label, children, onClick, disabled, danger }) {
  return (
    <Tooltip title={label}>
      <span>
        <Box
          component='button'
          type='button'
          aria-label={label}
          disabled={disabled}
          onClick={onClick}
          sx={{
            border: 'none', background: 'none', cursor: disabled ? 'default' : 'pointer',
            opacity: disabled ? 0.35 : 1,
            color: danger ? 'error.main' : 'text.secondary',
            display: 'flex', alignItems: 'center', p: '5px', borderRadius: '6px',
            '&:hover': { bgcolor: disabled ? 'transparent' : 'action.hover' },
          }}
        >
          {children}
        </Box>
      </span>
    </Tooltip>
  );
}

/** A dashed outline over a section, used for hover. */
function SectionOutline({ hostRef, id, color, dashed }) {
  const [box, setBox] = useState(null);
  useEffect(() => {
    const host = hostRef.current;
    const el = host?.querySelector(`[data-section-id="${id}"]`);
    if (!host || !el) { setBox(null); return; }
    const h = host.getBoundingClientRect();
    const b = el.getBoundingClientRect();
    setBox({ top: b.top - h.top, left: b.left - h.left, width: b.width, height: b.height });
  }, [hostRef, id]);

  if (!box) return null;
  return (
    <Box
      sx={{
        position: 'absolute',
        top: box.top, left: box.left, width: box.width, height: box.height,
        outline: `2px ${dashed ? 'dashed' : 'solid'} ${color}`,
        outlineOffset: '-2px',
        pointerEvents: 'none',
      }}
    />
  );
}
